import { call } from "@saas/api-client";
import { Alert, Button, Card, DescriptionList, ErrorAlert, Loading, PageHeader, TextField, useAsync, useCommand } from "@saas/ui";
import { useState, type FormEvent } from "react";
import { api } from "./api";

export function SettingsPage() {
  const tenant = useAsync(() => call(api.GET("/api/my-tenant")), []);

  if (tenant.loading && !tenant.data) return <Loading />;
  return (
    <>
      <PageHeader title="Settings" description="How your organisation appears to its users." />
      <ErrorAlert error={tenant.error} />
      {tenant.data && (
        <div className="sb-grid">
          {/* Keyed by tenant: the form starts from the loaded values (no effect copying data into state). */}
          <SettingsForm key={tenant.data.id} initialDisplayName={tenant.data.displayName} initialSupportEmail={tenant.data.supportEmail ?? ""} onSaved={tenant.reload} />
          <Card title="Tenant">
            <DescriptionList
              items={[
                ["Name", tenant.data.name],
                ["Slug", <code key="slug">{tenant.data.slug}</code>],
                ["Tenant id", <code key="id">{tenant.data.id}</code>],
                ["Status", tenant.data.status],
              ]}
            />
          </Card>
        </div>
      )}
    </>
  );
}

function SettingsForm({ initialDisplayName, initialSupportEmail, onSaved }: { initialDisplayName: string; initialSupportEmail: string; onSaved: () => void }) {
  const command = useCommand();
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const [supportEmail, setSupportEmail] = useState(initialSupportEmail);
  const [saved, setSaved] = useState(false);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaved(false);
    if (await command.run(() => call(api.PUT("/api/my-tenant/settings", { body: { displayName, supportEmail: supportEmail || null } })))) {
      setSaved(true);
      onSaved();
    }
  };

  return (
    <Card title="Organisation">
      <form className="sb-form" onSubmit={save} noValidate>
        <TextField label="Display name" required value={displayName} error={command.fieldError("displayName")} onChange={(e) => setDisplayName(e.target.value)} />
        <TextField
          label="Support email"
          type="email"
          hint="Shown to your users. Optional."
          value={supportEmail}
          error={command.fieldError("supportEmail")}
          onChange={(e) => setSupportEmail(e.target.value)}
        />
        {command.error && !command.fieldError("displayName") && !command.fieldError("supportEmail") && <ErrorAlert error={command.error} />}
        {saved && <Alert tone="success">Settings saved.</Alert>}
        <div>
          <Button type="submit" variant="primary" disabled={command.pending}>
            Save settings
          </Button>
        </div>
      </form>
    </Card>
  );
}
